import { type FormEvent, type ReactNode, useId, useState } from 'react';
import { t } from '../i18n';
import { toastError } from './Toasts';
import { Button } from './ui/Button';
import { Sheet } from './ui/Sheet';

/**
 * One-button confirm dialog.
 * Replaces window.confirm() with a styled, dismissable modal.
 */
export function ConfirmModal({
  title,
  body,
  confirmLabel,
  destructive,
  onConfirm,
  onClose,
}: {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}) {
  const titleId = useId();
  const [busy, setBusy] = useState(false);
  return (
    <Sheet onClose={onClose} closeOnBackdrop title={title} titleId={titleId}>
      {body && <div className="text-sm text-foreground/80">{body}</div>}
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          {t('Cancel')}
        </Button>
        <Button
          variant={destructive ? 'danger' : 'primary'}
          disabled={busy}
          onClick={async () => {
            if (busy) return;
            setBusy(true);
            try {
              // Same contract as PromptModal's submit below: the modal only
              // closes once the caller's own work actually finished. A
              // rejected confirm used to close anyway — indistinguishable
              // from success, with the failure lost as an unhandled
              // rejection instead of shown to the person.
              await onConfirm();
              onClose();
            } catch (err) {
              toastError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          {confirmLabel ?? t('Continue')}
        </Button>
      </div>
    </Sheet>
  );
}

/**
 * Multiline prompt modal. Returns the entered text via onSubmit, or null
 * if dismissed.
 */
export function PromptModal({
  title,
  placeholder,
  defaultValue,
  required = true,
  onSubmit,
  onClose,
}: {
  title: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
  onSubmit: (value: string) => void | Promise<void>;
  onClose: () => void;
}) {
  const [value, setValue] = useState(defaultValue ?? '');
  const [busy, setBusy] = useState(false);
  const titleId = useId();
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (required && !value.trim()) return;
    setBusy(true);
    try {
      // Some callers' submit is a server call the caller itself marks
      // optional in this UI (e.g. "why reject?") but that the server
      // actually requires a value for — a caller whose promise rejects
      // used to be indistinguishable from one that succeeded, since this
      // closed unconditionally right after calling onSubmit. Now the modal
      // only closes once the caller's own work has actually finished
      // without throwing; a rejection surfaces here and the user gets
      // another chance instead of a silently stuck request.
      await onSubmit(value.trim());
      onClose();
    } catch (err) {
      toastError(err);
      setBusy(false);
    }
  }
  return (
    <Sheet onClose={onClose} onSubmit={submit} closeOnBackdrop title={title} titleId={titleId}>
      <textarea
        // biome-ignore lint/a11y/noAutofocus: PromptModal is opened by an explicit user action and exists only to collect this one value, so focus belongs in the textarea
        autoFocus
        className="input min-h-[120px]"
        placeholder={placeholder}
        aria-label={title}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          {t('Cancel')}
        </Button>
        <Button type="submit" disabled={busy || (required && !value.trim())}>
          {t('Continue')}
        </Button>
      </div>
    </Sheet>
  );
}
